/** Chat-only text sizing, persisted per device and applied through a root data attribute. */

export type ChatTextSize = "compact" | "default" | "large" | "xlarge";

export const CHAT_TEXT_SIZES: readonly ChatTextSize[] = ["compact", "default", "large", "xlarge"];

export function isChatTextSize(value: string): value is ChatTextSize {
  return value === "compact" || value === "default" || value === "large" || value === "xlarge";
}

export const CHAT_TEXT_SIZE_STORAGE_KEY = "rakazo.chatTextSize";
export const CHAT_TEXT_SIZE_ATTRIBUTE = "data-chat-text-size";

type ChatTextSizeStorage = Pick<Storage, "getItem" | "setItem">;

const listeners = new Set<() => void>();
let memoryPreference: ChatTextSize | null = null;

function getLocalStorage(): ChatTextSizeStorage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

export function normalizeChatTextSize(raw: string | null | undefined): ChatTextSize {
  switch (raw?.trim().toLowerCase()) {
    case "compact":
      return "compact";
    case "large":
      return "large";
    case "xlarge":
    case "extra-large":
    case "extra large":
      return "xlarge";
    default:
      return "default";
  }
}

export function readChatTextSize(
  storage: Pick<Storage, "getItem"> | null = getLocalStorage(),
): ChatTextSize {
  if (!storage) return "default";
  try {
    return normalizeChatTextSize(storage.getItem(CHAT_TEXT_SIZE_STORAGE_KEY));
  } catch {
    return "default";
  }
}

export function applyChatTextSize(
  preference: ChatTextSize,
  root: HTMLElement | null = typeof document !== "undefined" ? document.documentElement : null,
): ChatTextSize {
  const normalized = normalizeChatTextSize(preference);
  if (root) root.dataset.chatTextSize = normalized;
  return normalized;
}

export function getChatTextSize(): ChatTextSize {
  return memoryPreference ?? readChatTextSize();
}

export function setChatTextSize(
  preference: ChatTextSize,
  storage: Pick<Storage, "setItem"> | null = getLocalStorage(),
): ChatTextSize {
  const normalized = normalizeChatTextSize(preference);
  memoryPreference = normalized;
  if (storage) {
    try {
      storage.setItem(CHAT_TEXT_SIZE_STORAGE_KEY, normalized);
    } catch {
      // Private browsing etc: keep the in-memory value.
    }
  }
  applyChatTextSize(normalized);
  for (const listener of listeners) listener();
  return normalized;
}

export function subscribeChatTextSize(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
